import classNames from 'classnames'
import { JSX, ReactNode, useCallback } from 'react'

import {
  AbsolutePopup,
  TPopupHorizontal,
  TPopupVertical,
  TVisibilityTrigger,
} from 'core-components/library/popup'

import styles from './Tooltip.module.scss'

export const ARROW_SIZE = 8
export const ARROW_SPACE = 4

export interface ITooltipProps {
  /**
   * A Class name for component root node
   */
  className?: string
  /**
   * A Class name for a popupWrap
   */
  popupWrapClassName?: string
  /**
   * A Class name for a tooltip bubble
   */
  viewClassName?: string
  /**
   * The content inside the tooltip bubble
   */
  tipContent: () => JSX.Element
  /**
   * Element a tooltip is applied to
   */
  children: ReactNode
  /**
   * Vertical direction of the tooltip
   */
  verticalDirection?: TPopupVertical
  /**
   * Horizontal direction of the tooltip
   */
  horizontalDirection?: TPopupHorizontal
  /**
   * Horizontal start position of the tooltip
   */
  horizontalStartPosition?: TPopupHorizontal
  /**
   * A disabled tooltip will not be displayed
   */
  disabled?: boolean
  /**
   * Enables inline-block layout
   */
  inline?: boolean
  /**
   * Text alignment inside the tooltip bubble
   */
  textAlign?: 'left' | 'right' | 'center'
  /**
   * Change the action type to show popup
   */
  visibilityTrigger?: TVisibilityTrigger
  /**
   * Callback function fires when popup visibility changed
   */
  visibilityCallback?: (newValue: boolean) => boolean
  /**
   * Root test ID. This property is used for automated tests.
   */
  __rootTestId?: string
}

export const Tooltip = ({
  children,
  tipContent,
  inline,
  disabled,
  viewClassName = '',
  className = '',
  popupWrapClassName = '',
  textAlign = 'center',
  horizontalStartPosition,
  verticalDirection = TPopupVertical.Top,
  horizontalDirection = TPopupHorizontal.Right,
  visibilityTrigger = TVisibilityTrigger.Hover,
  visibilityCallback,
  __rootTestId,
}: ITooltipProps) => {
  const popupClassName = classNames(
    {
      [styles['root__wrap-ver']]: true,
      [styles['root__wrap-hor']]: false,
    },
    popupWrapClassName || '',
    styles['wrap_z_index']
  )

  const rootClassName = classNames(
    styles.root,
    className,
    inline ? styles['root--inline-block'] : ''
  )

  const hDirection = horizontalDirection
  let vDirection = verticalDirection
  const hStartPosition =
    horizontalStartPosition ||
    (hDirection === TPopupHorizontal.Right ? TPopupHorizontal.Left : TPopupHorizontal.Right)

  if (horizontalDirection === hStartPosition && hStartPosition !== TPopupHorizontal.Center) {
    vDirection =
      verticalDirection === TPopupVertical.Top ? TPopupVertical.Bottom : TPopupVertical.Top
  }
  const isCenterHorizontalSide =
    horizontalStartPosition === TPopupHorizontal.Center &&
    horizontalDirection !== TPopupHorizontal.Center

  const visibilityCallbackInner = useCallback(
    (newValue: boolean) => {
      if (disabled === true) {
        return false
      }
      if (typeof visibilityCallback === 'function') {
        return visibilityCallback(newValue)
      }
      return newValue
    },
    [disabled, visibilityCallback]
  )

  return (
    <AbsolutePopup
      useOwnContext={false}
      popupClassName={popupClassName}
      rootClassName={rootClassName}
      verticalDirection={vDirection}
      horizontalDirection={hDirection}
      horizontalStartPosition={hStartPosition}
      verticalStartPosition={verticalDirection}
      visibilityTrigger={visibilityTrigger}
      horizontalOffset={isCenterHorizontalSide ? -(ARROW_SPACE + ARROW_SIZE / 2) : 0}
      visibilityCallback={visibilityCallbackInner}
      popupContent={(contentData) => {
        const { direction: currentDirection, startPosition: currentStartPosition } = contentData
        const { horizontal, vertical } = currentDirection
        const { horizontal: startHorizontal, vertical: startVertical } = currentStartPosition
        const isSide = horizontalStartPosition === horizontal && horizontal !== 'center'

        return (
          <div
            className={classNames(
              styles.tooltip,
              isSide
                ? styles[`arrow-side-${startHorizontal}-position`]
                : styles[`arrow-horizontal-${horizontal}-position`],
              isSide
                ? styles[`arrow-side-${startVertical}-position`]
                : styles[`arrow-vertical-${vertical}-position`],
              styles[`tooltip--text-${textAlign}`],
              viewClassName
            )}
          >
            <div className={styles.tooltipContent}>{tipContent()}</div>
          </div>
        )
      }}
      __rootTestId={__rootTestId || 'tooltipRoot'}
    >
      {children}
    </AbsolutePopup>
  )
}
